import {repositoryPath} from '@github-ui/paths'
import {DEFAULT_FILTER_PRIORITY, FilterValueType} from '@github-ui/filter'
import type {
  FilterQuery,
  FilterProvider,
  AnyBlock,
  MutableFilterBlock,
  FilterConfig,
  ARIAFilterSuggestion,
  FilterValueData,
  ValueRowProps,
  IndexedBlockValueItem,
  FilterKey,
} from '@github-ui/filter'
import {AsyncFilterProvider} from '@github-ui/filter/providers'
import {getFilterValue, getUnescapedFilterValue} from '@github-ui/filter/utils'
import {ValueIcon} from '@github-ui/filter/utils/ValueIcon'
import {GitBranchIcon} from '@primer/octicons-react'
import {singleValueFilterTypes} from '../SecurityProductsFilterBar'
import {debounce} from '@github/mini-throttle'

const BRANCH_FILTER_KEY: FilterKey = {
  displayName: 'Branch',
  key: 'branch',
  priority: DEFAULT_FILTER_PRIORITY,
  icon: GitBranchIcon,
  description: 'Filter by branch name',
}

type BranchRef = {
  ref: string
}

type RefsResponse = {
  refs: string[]
  cacheKey: string
}

const DEBOUNCE_DELAY = 150

export class BranchFilterProvider extends AsyncFilterProvider<BranchRef> implements FilterProvider {
  #debouncedFetch: ((value: string) => void) | null = null

  constructor(owner: string, repo: string) {
    super(BRANCH_FILTER_KEY, {filterTypes: singleValueFilterTypes})

    const refsEndpoint = repositoryPath({owner, repo, action: 'refs'})
    this.suggestionEndpoint = refsEndpoint
    this.validationEndpoint = refsEndpoint
  }

  async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    _config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)
  }

  #processSuggestion(branch: BranchRef, _query: string): ARIAFilterSuggestion {
    const branchName = branch.ref

    return {
      type: FilterValueType.Value,
      displayName: branchName,
      ariaLabel: branchName,
      value: branchName,
      icon: this.icon,
      priority: BRANCH_FILTER_KEY.priority,
    }
  }

  override async fetchSuggestions(
    value: string,
    _block: AnyBlock | MutableFilterBlock,
    _filterQuery: FilterQuery,
  ): Promise<BranchRef[] | null> {
    if (this.suggestionCache.has(value)) {
      return this.suggestionCache.get(value) ?? []
    }

    return new Promise(resolve => {
      if (!this.#debouncedFetch) {
        this.#debouncedFetch = debounce(async (searchValue: string) => {
          const result = await this.#doFetch(searchValue)
          resolve(result)
        }, DEBOUNCE_DELAY)
      }
      this.#debouncedFetch(value)
    })
  }

  async #doFetch(value: string): Promise<BranchRef[] | null> {
    if (!this.suggestionEndpoint) {
      return null
    }

    try {
      const endpoint = new URL(this.suggestionEndpoint, window.location.origin)
      endpoint.searchParams.set('type', 'branch')

      const response = await this.fetchData(endpoint.toString())
      if (!response || !response.ok) {
        return null
      }

      const json = (await response.json()) as RefsResponse
      const refs = json.refs ?? []

      const branchRefs: BranchRef[] = refs
        .filter((ref): ref is string => typeof ref === 'string')
        .filter(ref => !value || ref.toLowerCase().includes(value.toLowerCase()))
        .map(ref => ({ref}))

      this.suggestionCache.set(value, branchRefs)
      return branchRefs
    } catch {
      return null
    }
  }

  override validateValue(
    _filterQuery: FilterQuery,
    value: IndexedBlockValueItem,
    branch: BranchRef | null,
  ): false | Partial<IndexedBlockValueItem> | null | undefined {
    const extractedValue = getUnescapedFilterValue(value.value)
    if (!branch || !extractedValue) return false

    return {
      value: extractedValue,
      displayName: branch.ref,
    }
  }

  getValueRowProps(value: FilterValueData): ValueRowProps {
    return {
      text: value.displayName ?? getFilterValue(value.value) ?? '',
      leadingVisual: ValueIcon({providerIcon: this.icon, value}),
    }
  }
}
